import React from "react";
import { Link } from "react-router-dom";
import { useTranslation } from "react-i18next";
import {
  HomeIcon,
  MagnifyingGlassIcon,
  ArrowRightIcon,
  MapPinIcon,
} from "@heroicons/react/24/outline";
import { SparklesIcon } from "@heroicons/react/24/solid";

/**
 * 404 — redesigned to match the Tailwind design system used on
 * Home/Contact/FAQ. Replaces the legacy MUI Banner + SVG illustration with a
 * large brand-styled "404" display, two CTAs (home + explore) and a few
 * helpful quick links.
 */

const QUICK_LINKS = [
  { to: "/events", key: "notFound.linkEvents", def: "Toutes les expériences" },
  { to: "/become-partner", key: "notFound.linkPartner", def: "Devenir partenaire" },
  { to: "/faqs", key: "notFound.linkFaq", def: "Questions fréquentes" },
  { to: "/contact", key: "notFound.linkContact", def: "Nous contacter" },
];

export default function NotFound() {
  const { t } = useTranslation();

  return (
    <div className="relative overflow-hidden bg-surface-muted">
      {/* soft accent halos */}
      <div
        aria-hidden
        className="pointer-events-none absolute -top-24 start-[-6rem] h-72 w-72 rounded-full bg-brand-500/10 blur-3xl"
      />
      <div
        aria-hidden
        className="pointer-events-none absolute -bottom-24 end-[-6rem] h-72 w-72 rounded-full bg-accent-500/20 blur-3xl"
      />

      <div className="relative mx-auto flex min-h-[70vh] w-full max-w-container flex-col items-center justify-center px-4 py-16 text-center sm:px-6 sm:py-20 lg:px-8">
        {/* Big 404 with a location-pin swap for the 0 */}
        <div className="flex items-center justify-center gap-2 sm:gap-3">
          <span className="font-display text-[5.5rem] font-extrabold leading-none tracking-tight text-brand-500 sm:text-[8rem]">
            4
          </span>
          <span className="relative flex h-24 w-24 items-center justify-center rounded-full bg-white shadow-elevated ring-1 ring-slate-200/80 sm:h-32 sm:w-32">
            <MapPinIcon className="h-12 w-12 text-brand-400 sm:h-16 sm:w-16" />
            <span className="absolute -right-1 -top-1 flex h-8 w-8 items-center justify-center rounded-full bg-accent-500 text-base font-bold text-brand-900 shadow-card sm:h-9 sm:w-9">
              <SparklesIcon className="h-4 w-4" />
            </span>
          </span>
          <span className="font-display text-[5.5rem] font-extrabold leading-none tracking-tight text-brand-500 sm:text-[8rem]">
            4
          </span>
        </div>

        <h1 className="mt-8 font-display text-2xl font-extrabold tracking-tight text-ink sm:text-3xl lg:text-4xl">
          {t("notFound.title", "Oups, cette page est introuvable")}
        </h1>
        <p className="mt-3 max-w-md text-sm leading-relaxed text-ink-muted sm:text-base">
          {t(
            "notFound.subtitle",
            "La page que vous cherchez a peut-être été déplacée ou n'existe plus. Pas d'inquiétude, il reste des milliers d'expériences à découvrir."
          )}
        </p>

        {/* CTAs */}
        <div className="mt-8 flex w-full flex-col items-center justify-center gap-3 sm:w-auto sm:flex-row">
          <Link
            to="/"
            className="inline-flex h-12 w-full items-center justify-center gap-2 rounded-full bg-brand-500 px-6 font-semibold text-white shadow-card transition-colors hover:bg-brand-600 focus:outline-none focus-visible:ring-2 focus-visible:ring-brand-500/30 sm:w-auto"
          >
            <HomeIcon className="h-4 w-4" />
            <span>{t("notFound.ctaHome", "Retour à l'accueil")}</span>
          </Link>
          <Link
            to="/events"
            className="inline-flex h-12 w-full items-center justify-center gap-2 rounded-full border border-slate-200 bg-white px-6 font-semibold text-ink shadow-card transition-colors hover:border-slate-300 hover:bg-surface-subtle sm:w-auto"
          >
            <MagnifyingGlassIcon className="h-4 w-4" />
            <span>{t("notFound.ctaExplore", "Explorer les expériences")}</span>
          </Link>
        </div>

        {/* Quick links */}
        <div className="mt-12 w-full max-w-lg">
          <p className="text-xs font-semibold uppercase tracking-wide text-ink-subtle">
            {t("notFound.quickLinksTitle", "Liens utiles")}
          </p>
          <div className="mt-4 grid grid-cols-1 gap-2 sm:grid-cols-2">
            {QUICK_LINKS.map((link) => (
              <Link
                key={link.to}
                to={link.to}
                className="group flex items-center justify-between gap-2 rounded-xl border border-slate-100 bg-white px-4 py-3 text-start shadow-card transition-all hover:border-brand-200 hover:shadow-card-hover"
              >
                <span className="text-sm font-medium text-ink">
                  {t(link.key, link.def)}
                </span>
                <ArrowRightIcon className="h-4 w-4 shrink-0 text-ink-subtle transition-colors group-hover:text-brand-500 rtl:-scale-x-100" />
              </Link>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
}
